import { create } from "zustand"

export type FlushOutcome = "idle" | "saved" | "conflict" | "error"

/** What the page needs to know about one mounted section editor. */
export interface EditHandle {
  threadId: string
  /** The user has typed something that is not saved yet (including text still inside the editor's debounce). */
  isDirty: () => boolean
  /** Deliver the editor's pending text and save it now. */
  flush: () => Promise<FlushOutcome>
  /** Send the unsaved text with a keepalive request (page is closing). */
  beacon: () => void
}

const handles = new Map<string, EditHandle>()
let installed = false

function onBeforeUnload(e: BeforeUnloadEvent) {
  const dirty = [...handles.values()].filter((h) => h.isDirty())
  if (!dirty.length) return
  dirty.forEach((h) => h.beacon())
  e.preventDefault()
  e.returnValue = "" // the browser shows its own "leave site?" prompt
}

/** Register a section editor; returns the unregister function. */
export function registerEdit(key: string, handle: EditHandle): () => void {
  if (!installed && typeof window !== "undefined") {
    window.addEventListener("beforeunload", onBeforeUnload)
    installed = true
  }
  handles.set(key, handle)
  return () => { if (handles.get(key) === handle) handles.delete(key) }
}

/**
 * Save every pending edit on a thread. Resolves with the worst outcome:
 * "conflict" or "error" means something is still unsaved.
 */
export async function flushThreadEdits(threadId: string): Promise<FlushOutcome> {
  const outcomes = await Promise.all([...handles.values()].filter((h) => h.threadId === threadId).map((h) => h.flush()))
  if (outcomes.includes("conflict")) return "conflict"
  if (outcomes.includes("error")) return "error"
  return outcomes.includes("saved") ? "saved" : "idle"
}

interface Recovered { markdown: string; baseVersion: number }

interface RecoveredState {
  /** Edits that could not be saved after their editor went away, keyed `${threadId}:${sectionId}`. */
  byKey: Record<string, Recovered>
  stash: (key: string, edit: Recovered) => void
  peek: (key: string) => Recovered | undefined
  drop: (key: string) => void
  clear: () => void
}

export const useRecoveredEdits = create<RecoveredState>((set, get) => ({
  byKey: {},
  stash: (key, edit) => set((s) => ({ byKey: { ...s.byKey, [key]: edit } })),
  peek: (key) => get().byKey[key],
  drop: (key) => set((s) => { const { [key]: _gone, ...rest } = s.byKey; void _gone; return { byKey: rest } }),
  clear: () => set({ byKey: {} }),
}))
